import Select, { ReactSelectOption, SelectedType } from "@jmc/solid-design-system/src/components/atoms/Select/Select";
import React from "react";
import { createRoot } from "react-dom/client";

/**
 * Finds and replaces all native HTML select elements matching the provided selector with the Select component.
 * The function maintains the original select element's values and options while hiding it, and replaces them by the Select
 * component that mirrors its functionality.
 *
 * @param {string} selector - A CSS selector string to target the select elements (e.g., ".custom-select" or "#mySelect")
 * @returns {void}
 *
 * @example
 * // Replace all select elements with class 'custom-select'
 * findAndReplaceDomSelect('.custom-select');
 *
 * // Replace a specific select element by ID
 * findAndReplaceDomSelect('#mySelectElement');
 */
export const findAndReplaceDomSelect = (selector: string): void => {
    const selectElements = document.querySelectorAll(selector);

    if (!selectElements?.length) {
        return;
    }

    [...selectElements].forEach((selectDiv: HTMLDivElement) => {
        const select = selectDiv.getElementsByTagName("select")[0] as HTMLSelectElement;

        select.setAttribute("aria-hidden", "true");
        select.style.position = "absolute";
        select.style.width = "1px";
        select.style.height = "1px";
        select.style.padding = "0";
        select.style.margin = "-1px";
        select.style.overflow = "hidden";
        select.style.clip = "rect(0, 0, 0, 0)";
        select.style.whiteSpace = "nowrap";
        select.style.border = "0";

        const options = [...select.options].map((option: HTMLOptionElement) => ({
            value: option.value,
            text: option.text,
            id: option.id,
            isSelected: option.selected,
        }));

        // Remove any existing div elements
        const existingDivs = selectDiv.getElementsByTagName("div");
        while (existingDivs.length > 0) {
            existingDivs[0].remove();
        }

        const container = document.createElement("div");
        selectDiv.appendChild(container);

        const root = createRoot(container);
        root.render(
            <Select
                name={select.name}
                onChange={(selected: SelectedType) => {
                    setSourceSelectValue(select, selected);
                    findAndReplaceDomSelect(selector);
                }}
            >
                {options.map((option) => (
                    <Select.Option key={option.id} value={option.value} selected={select.value === option.value}>
                        {option.text}
                    </Select.Option>
                ))}
            </Select>,
        );
    });
};

const setSourceSelectValue = (select: HTMLSelectElement, selected: SelectedType): void => {
    if (!selected) {
        select.value = "";
        return;
    }

    // Handle the selected value (assuming it's a single select)
    const selectedValue = (selected as ReactSelectOption).value;
    select.value = selectedValue as string;

    // Trigger change event to ensure form state is updated
    const event = new Event("change", { bubbles: true });
    select.dispatchEvent(event);
};
